import React, { useRef, useState } from 'react';
import { Drawer, Button, Input, Select, Tag, Space, Typography, Alert, Empty, Spin, Modal, Checkbox, message } from 'antd';
import { useResource } from '../../hooks/useResource';
import { openServerUrl, preopenWindow, userMessageForError } from '../../utils/apiError';
import { reportOpenFailure } from '../../utils/openLinkFeedback';
import {
    SUPPORT_PRIORITIES,
    SUPPORT_STATUSES,
    PRIORITY_COLOR,
    STATUS_COLOR,
    humanizeEnum,
    formatDdMmYyyy,
    formatDdMmYyyyTime,
    shortId,
    resolutionSlaState,
    validateAttachmentFile,
    ATTACHMENT_ACCEPT,
    MAX_ATTACHMENTS_PER_TICKET,
    MESSAGE_BODY_MAX_CHARS,
} from '../../utils/supportApi';

const { Text, Paragraph, Title } = Typography;

const SLA_TAG = {
    breached: { color: 'red', text: 'SLA breached' },
    overdue: { color: 'volcano', text: 'Resolution overdue' },
    open: { color: 'default', text: 'Within SLA' },
    done: { color: 'success', text: 'Closed within SLA' },
};

/**
 * Ticket detail + conversation. Shared by the platform_admin console (`staff`) and the
 * customer's own-tickets page. `staff` only decides which CONTROLS render — the server is
 * the real control: PATCH is route-gated to platform_admin/support_agent, internal notes are
 * only returned to (and only creatable by) support staff, and a reporter only ever resolves
 * their own tenant's own tickets (supportTicket.service.ts `loadTicketForActor`).
 */
const TicketDetailDrawer = ({ open, ticket, staff, api, myUserId, staffList, projectLabel, onClose, onTicketChanged, onActivity }) => {
    const ticketId = ticket ? ticket.id : null;
    const messages = useResource(() => api.listMessages(ticketId), [api, ticketId], { enabled: open && !!ticketId });
    const attachments = useResource(() => api.listAttachments(ticketId), [api, ticketId], { enabled: open && !!ticketId });

    const [reply, setReply] = useState('');
    const [internal, setInternal] = useState(false);
    const [sending, setSending] = useState(false);
    const [patching, setPatching] = useState(false);
    const [uploading, setUploading] = useState(false);
    const [confirmClose, setConfirmClose] = useState(false);
    const [downloadingId, setDownloadingId] = useState(null);
    const fileRef = useRef(null);

    if (!ticket) return null;

    const patch = async (fields, okText) => {
        setPatching(true);
        try {
            const updated = await api.patchTicket(ticket.id, fields);
            onTicketChanged(updated);
            message.success(okText);
        } catch (err) {
            message.error(err.message);
        } finally {
            setPatching(false);
        }
    };

    const send = async () => {
        if (!reply.trim()) return;
        setSending(true);
        try {
            await api.addMessage(ticket.id, staff ? { body: reply, isInternal: internal } : { body: reply });
            setReply('');
            setInternal(false);
            messages.reload();
            // A late first response flips the ticket's SLA state on the server; refresh the row.
            if (onActivity) onActivity();
        } catch (err) {
            message.error(err.message);
        } finally {
            setSending(false);
        }
    };

    const upload = async (e) => {
        const file = e.target.files && e.target.files[0];
        if (fileRef.current) fileRef.current.value = '';
        if (!file) return;
        const problem = validateAttachmentFile(file, attachments.data ? attachments.data.length : 0);
        if (problem) {
            message.error(problem);
            return;
        }
        setUploading(true);
        try {
            await api.uploadAttachment(ticket.id, file);
            attachments.reload();
            message.success('Attachment uploaded.');
        } catch (err) {
            message.error(err.message);
        } finally {
            setUploading(false);
        }
    };

    const download = async (a) => {
        setDownloadingId(a.id);
        const win = preopenWindow();
        try {
            const d = await api.getAttachmentDownload(ticket.id, a.id);
            if (!openServerUrl(d?.url, win)) reportOpenFailure(message, d?.url);
        } catch (err) {
            if (win && !win.closed) win.close();
            message.error(userMessageForError(err, 'Could not download the attachment.'));
        } finally {
            setDownloadingId(null);
        }
    };

    const sla = SLA_TAG[resolutionSlaState(ticket)];
    const staffById = new Map((staffList || []).map((u) => [u.id, u]));
    const assigneeText = !ticket.assignedToUserId
        ? 'Unassigned'
        : !staff
          ? 'Assigned to Prolense support'
          : ticket.assignedToUserId === myUserId
            ? 'You'
            : staffById.has(ticket.assignedToUserId)
              ? staffById.get(ticket.assignedToUserId).name
              : `User ${shortId(ticket.assignedToUserId)}`;

    const renderMessages = () => {
        if (messages.status === 'loading' || messages.status === 'idle') return <Spin />;
        if (messages.status === 'error') {
            return <Alert type="error" showIcon message="Could not load the conversation" description={messages.error.message} />;
        }
        if (!messages.data || messages.data.length === 0) {
            return <Empty description="No replies yet" />;
        }
        return messages.data.map((m) => (
            <div key={m.id} style={{ marginBottom: 12, padding: 12, borderRadius: 8, background: m.isInternal ? 'var(--color-warn-bg)' : 'var(--color-surface-sunken)' }}>
                <Space size={8} wrap>
                    <Text strong>{m.authorRole === 'support_agent' ? 'Prolense support' : 'Reporter'}</Text>
                    {m.isInternal && <Tag color="gold">Internal note - not visible to the reporter</Tag>}
                    <Text type="secondary">{formatDdMmYyyyTime(m.createdAt)}</Text>
                </Space>
                <Paragraph style={{ margin: '6px 0 0', whiteSpace: 'pre-wrap' }}>{m.body}</Paragraph>
            </div>
        ));
    };

    const renderAttachments = () => {
        if (attachments.status === 'loading' || attachments.status === 'idle') return <Spin />;
        if (attachments.status === 'error') {
            return <Alert type="error" showIcon message="Could not load attachments" description={attachments.error.message} />;
        }
        if (!attachments.data || attachments.data.length === 0) return <Text type="secondary">No attachments.</Text>;
        return attachments.data.map((a) => (
            <div key={a.id}>
                <Text>{a.filename}</Text>{' '}
                <Text type="secondary">
                    ({Math.max(1, Math.round(a.sizeBytes / 1024))} KB, {formatDdMmYyyy(a.createdAt)})
                </Text>{' '}
                <Button size="small" loading={downloadingId === a.id} onClick={() => download(a)}>
                    Download {a.filename}
                </Button>
            </div>
        ));
    };

    return (
        <Drawer open={open} onClose={onClose} width={640} title={ticket.title}>
            <Space size={8} wrap style={{ marginBottom: 12 }}>
                <Tag color={STATUS_COLOR[ticket.status]}>{humanizeEnum(ticket.status)}</Tag>
                <Tag color={PRIORITY_COLOR[ticket.priority]}>{humanizeEnum(ticket.priority)}</Tag>
                <Tag>{humanizeEnum(ticket.category)}</Tag>
                <Tag color={sla.color}>{sla.text}</Tag>
            </Space>

            <div style={{ display: 'grid', gridTemplateColumns: '150px 1fr', rowGap: 6, marginBottom: 16 }}>
                <Text type="secondary">Ticket</Text>
                <Text copyable={{ text: ticket.id }}>{shortId(ticket.id)}</Text>
                {staff && (
                    <>
                        <Text type="secondary">Tenant</Text>
                        <Text copyable={{ text: ticket.tenantId || '' }}>
                            {ticket.tenantId ? `${ticket.tenantName || 'Unnamed tenant'} (${shortId(ticket.tenantId)})` : 'Platform-level (no tenant)'}
                        </Text>
                        <Text type="secondary">Reporter</Text>
                        <Text copyable={{ text: ticket.reporterUserId }}>
                            {ticket.reporterName || `User ${shortId(ticket.reporterUserId)}`}
                            {ticket.reporterEmail ? ` - ${ticket.reporterEmail}` : ''}
                        </Text>
                    </>
                )}
                <Text type="secondary">Project</Text>
                <Text>{ticket.projectId ? projectLabel || shortId(ticket.projectId) : '-'}</Text>
                <Text type="secondary">Assignee</Text>
                <Text>{assigneeText}</Text>
                <Text type="secondary">First response due</Text>
                <Text>{formatDdMmYyyyTime(ticket.slaFirstResponseDueAt)}</Text>
                <Text type="secondary">Resolution due</Text>
                <Text>{formatDdMmYyyyTime(ticket.slaResolutionDueAt)}</Text>
                <Text type="secondary">SLA tier at creation</Text>
                <Text>{ticket.slaTierAtCreate}</Text>
                <Text type="secondary">Raised</Text>
                <Text>{formatDdMmYyyyTime(ticket.createdAt)}</Text>
                <Text type="secondary">Last updated</Text>
                <Text>{formatDdMmYyyyTime(ticket.updatedAt)}</Text>
                <Text type="secondary">Resolved / closed</Text>
                <Text>{formatDdMmYyyyTime(ticket.resolvedAt)}</Text>
            </div>

            <Title level={5}>Description</Title>
            <Paragraph style={{ whiteSpace: 'pre-wrap' }}>{ticket.description}</Paragraph>

            {staff && (
                <div style={{ margin: '16px 0', padding: 12, border: '1px solid var(--color-border)', borderRadius: 8 }}>
                    <Title level={5} style={{ marginTop: 0 }}>Handle ticket</Title>
                    <Space wrap>
                        <Select
                            aria-label="Status"
                            style={{ width: 180 }}
                            value={ticket.status}
                            disabled={patching}
                            options={SUPPORT_STATUSES.map((s) => ({ value: s, label: humanizeEnum(s) }))}
                            onChange={(v) => {
                                if (v === ticket.status) return;
                                if (v === 'closed') setConfirmClose(true);
                                else patch({ status: v }, 'Status updated.');
                            }}
                        />
                        <Select
                            aria-label="Priority"
                            style={{ width: 140 }}
                            value={ticket.priority}
                            disabled={patching}
                            options={SUPPORT_PRIORITIES.map((p) => ({ value: p, label: humanizeEnum(p) }))}
                            onChange={(v) => v !== ticket.priority && patch({ priority: v }, 'Priority updated.')}
                        />
                        <Select
                            aria-label="Assignee"
                            style={{ width: 240 }}
                            placeholder="Unassigned"
                            allowClear
                            value={ticket.assignedToUserId || undefined}
                            disabled={patching || !staffList}
                            options={(staffList || []).map((u) => ({ value: u.id, label: `${u.name} (${humanizeEnum(u.role)})` }))}
                            onChange={(v) => {
                                const next = v || null;
                                if (next === (ticket.assignedToUserId || null)) return;
                                patch({ assignedToUserId: next }, next ? 'Ticket assigned.' : 'Ticket unassigned.');
                            }}
                        />
                        {ticket.status !== 'closed' && (
                            <Button danger disabled={patching} onClick={() => setConfirmClose(true)}>
                                Close ticket
                            </Button>
                        )}
                    </Space>
                </div>
            )}

            <Title level={5}>Conversation</Title>
            {renderMessages()}

            <div style={{ marginTop: 12 }}>
                <Input.TextArea
                    aria-label="Reply"
                    rows={3}
                    maxLength={MESSAGE_BODY_MAX_CHARS}
                    placeholder={staff ? 'Reply to the reporter, or record an internal note' : 'Write a reply to Prolense support'}
                    value={reply}
                    onChange={(e) => setReply(e.target.value)}
                />
                <Space style={{ marginTop: 8 }}>
                    {staff && (
                        <Checkbox checked={internal} onChange={(e) => setInternal(e.target.checked)}>
                            Internal note (hidden from the reporter)
                        </Checkbox>
                    )}
                    <Button type="primary" loading={sending} disabled={!reply.trim()} onClick={send}>
                        {staff && internal ? 'Add internal note' : 'Send reply'}
                    </Button>
                </Space>
            </div>

            <Title level={5} style={{ marginTop: 20 }}>Attachments</Title>
            {renderAttachments()}
            <div style={{ marginTop: 8 }}>
                <input ref={fileRef} type="file" accept={ATTACHMENT_ACCEPT} aria-label="Attach a file" onChange={upload} disabled={uploading} />
                <div><Text type="secondary">Allowed: {ATTACHMENT_ACCEPT.split(',').join(' ')} - at most {MAX_ATTACHMENTS_PER_TICKET} per ticket.</Text></div>
                {staff && <div><Text type="secondary">Attachments are visible to the reporter; there is no internal-only attachment.</Text></div>}
                {uploading && <Spin size="small" style={{ marginLeft: 8 }} />}
            </div>

            <Modal
                open={confirmClose}
                title="Close this ticket?"
                okText="Close ticket"
                okButtonProps={{ danger: true }}
                onCancel={() => setConfirmClose(false)}
                onOk={async () => {
                    setConfirmClose(false);
                    await patch({ status: 'closed' }, 'Ticket closed.');
                }}
            >
                Closing records a resolved date and is audit-logged. Tickets are never deleted; moving a closed ticket back to another status clears its resolved date.
            </Modal>
        </Drawer>
    );
};

export default TicketDetailDrawer;
